import { type ComponentProps, type FC, useId } from "react"
import { styled } from "styled-system/jsx"

const BaseInput = styled("input", {
  base: {
    appearance: "none",
    display: "inline-flex",
    alignItems: "center",
    height: "[32px]",
    minHeight: "[32px]",
    minWidth: "[55px]",
    borderRadius: "[4px]",
    borderWidth: "1px",
    border: true,
    backgroundColor: "[white]",
    paddingInline: "[12px]",
    outline: "[0]",
    textAlign: "start",
    textStyle: "input",
    transitionProperty:
      "[border-width, background-color, border-color, box-shadow]",
    transitionDuration: "[150ms]",
    _focusVisible: {
      outline: "[2px solid]",
      outlineColor: "outline",
    },
    _disabled: {
      cursor: "not-allowed",
      opacity: "muted",
    },
  },
})

const InputRoot = styled("div", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "xs",
  },
})

const InputLabel = styled("label", {
  base: {
    textStyle: "heading",
  },
})

export type InputProps = ComponentProps<typeof BaseInput>

// `children` is the input's label: the label is tied to the input by id, so a
// labelled input stays a single element at the call site.
export const Input: FC<InputProps> = ({ children, id, ...props }) => {
  const fallbackId = useId()

  if (!children) {
    return (
      <BaseInput
        id={id}
        {...props}
      />
    )
  }

  const inputId = id ?? fallbackId

  return (
    <InputRoot>
      <InputLabel htmlFor={inputId}>{children}</InputLabel>
      <BaseInput
        id={inputId}
        {...props}
      />
    </InputRoot>
  )
}
